import React, { useEffect, useState } from 'react';

interface TopTabsProps {
  sections: { key: string; title: string }[];
}

const TopTabs: React.FC<TopTabsProps> = ({ sections }) => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      
      // Find which section is currently in view
      const sectionElements = sections.map(section => ({
        key: section.key,
        element: document.getElementById(`${section.key}-anchor`)
      })).filter(s => s.element);

      if (sectionElements.length === 0) return;

      // Get the section that's most visible
      let currentSection = '';
      let maxVisibility = 0;

      sectionElements.forEach(({ key, element }) => {
        if (!element) return;
        
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Calculate visibility percentage
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(windowHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibility = visibleHeight / Math.min(rect.height, windowHeight);
        
        if (visibility > maxVisibility && visibility > 0.3) {
          maxVisibility = visibility;
          currentSection = key;
        }
      });

      // If no section is significantly visible, check which one is closest to the top
      if (!currentSection) {
        let closestSection = '';
        let minDistance = Infinity;

        sectionElements.forEach(({ key, element }) => {
          if (!element) return;
          
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestSection = key;
          }
        });

        currentSection = closestSection;
      }

      setActiveSection(currentSection);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50" dir="rtl">
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="flex items-center justify-center py-3 sm:py-4">
          <div className={`flex items-center gap-2 sm:gap-4 rounded-full transition-all ${scrolled ? 'bg-bg-primary/60 backdrop-blur px-3 sm:px-4 py-2 border border-white/10' : ''}`}>
            {sections.map((section) => (
              <button
                key={`top-tab-${section.key}`}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-sm sm:text-base font-bold font-madani transition-all duration-300 ${
                  activeSection === section.key
                    ? 'text-white bg-primary/80 shadow-lg scale-105'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => {
                  const el = document.getElementById(`${section.key}-anchor`);
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopTabs;


